import { normalizeHex } from './color-separation';

// Create Team Store pre-fill (spec 2026-10-22 §5). Once a sport is picked, that team's Scout
// identity (sports[sport_id].identity on GET /api/scout/schools/:id/sports: the colours and mascot
// a live store confirmed) wins over the school record, field by field. A colour the operator
// changed is never overwritten. Pure; CreateStore.tsx applies the result.

export type TeamIdentity = {
  colors?: { primary?: string | null; secondary?: string | null; tertiary?: string | null } | null;
  mascot?: string | null;
};
export type Colours = { primary: string; secondary: string; tertiary: string };
type SchoolColours = { primary_color?: string; secondary_color?: string; tertiary_color?: string };

const FIELDS = [
  ['primary', 'primary_color'],
  ['secondary', 'secondary_color'],
  ['tertiary', 'tertiary_color'],
] as const;

// sports[sportId].identity from the /sports payload ({ sports: { [key]: {...} } }). Looked up the
// way the admin forms do (lib/school-logos.js teamIdentityFor): the sports{} key itself, then an
// entry whose own sport_id is it, then v4's un-suffixed coed key (cross_country_coed →
// cross_country). The older array shape carries no identity.
export function teamIdentityOf(data: unknown, sportId: string): TeamIdentity | null {
  if (!sportId || !data || typeof data !== 'object') return null;
  const sports = (data as { sports?: unknown }).sports;
  if (!sports || typeof sports !== 'object' || Array.isArray(sports)) return null;
  const map = sports as Record<string, unknown>;
  const has = (key: string) => Object.prototype.hasOwnProperty.call(map, key);
  const coedKey = sportId.endsWith('_coed') ? sportId.slice(0, -'_coed'.length) : '';
  const entry = has(sportId)
    ? map[sportId]
    : Object.values(map).find((v) => !!v && typeof v === 'object' && (v as { sport_id?: unknown }).sport_id === sportId)
      ?? (coedKey && has(coedKey) ? map[coedKey] : undefined);
  const identity = entry && typeof entry === 'object' ? (entry as { identity?: unknown }).identity : null;
  return identity && typeof identity === 'object' && !Array.isArray(identity) ? (identity as TeamIdentity) : null;
}

// Each colour field shows the team's hex, else the school's, else ''. A field that is empty or
// still shows what the wizard last filled in follows; one the operator changed stays.
// → next: what to show; filled: what the wizard has now filled in (pass it back next time).
export function planColourPrefill({ current, filled, team, school }: {
  current: Colours;
  filled: Colours;
  team: TeamIdentity | null;
  school: SchoolColours | null;
}): { next: Colours; filled: Colours } {
  const next: Colours = { ...current };
  const nowFilled: Colours = { ...filled };
  for (const [role, field] of FIELDS) {
    const want = normalizeHex(team?.colors?.[role]) || normalizeHex(school?.[field]) || '';
    if (current[role] === '' || current[role] === filled[role]) {
      next[role] = want;
      nowFilled[role] = want;
    }
  }
  return { next, filled: nowFilled };
}

// The mascot the draft store is created with: the team's, else the school's.
export function prefillMascot(team: TeamIdentity | null, school: { mascot?: string } | null): string | undefined {
  const own = typeof team?.mascot === 'string' ? team.mascot.trim() : '';
  return own || school?.mascot || undefined;
}

// Which note the colours step shows under its heading: 'team' while a colour field still holds the
// value this team's identity gave it (once the operator has changed every one, the note would be
// untrue), 'school' when there is no team colour to show and the school record has a primary
// colour (as before), else ''. A team with colours supersedes the school note.
export function colourPrefillNote({ current, team, school }: {
  current: Colours;
  team: TeamIdentity | null;
  school: SchoolColours | null;
}): 'team' | 'school' | '' {
  const teamHexes = FIELDS.map(([role]) => normalizeHex(team?.colors?.[role]));
  if (teamHexes.some(Boolean)) {
    return FIELDS.some(([role], i) => !!teamHexes[i] && current[role] === teamHexes[i]) ? 'team' : '';
  }
  return school?.primary_color ? 'school' : '';
}
